import { createElement, lazy, Suspense, ComponentType } from "react";
import { createBrowserRouter, redirect } from "react-router";
import { useAuthStore } from "../stores/authStore";
import { AuthGuard } from "./components/AuthGuard";
import { MainLayout } from "./components/layout/MainLayout";
import { SkeletonPage } from "./components/Skeleton";
import { RouteErrorBoundary } from "./components/ErrorBoundary";

function lazyRoute<T extends ComponentType<any>>(
  factory: () => Promise<any>,
  getComp?: (mod: any) => T,
): ComponentType<any> {
  const LazyComp = lazy(async () => {
    const mod = await factory();
    const Component = getComp ? getComp(mod) : (mod.default || mod);
    return { default: Component };
  });

  return function SuspenseRouteWrapper(props: any) {
    return createElement(
      Suspense,
      { fallback: createElement(SkeletonPage) },
      createElement(LazyComp, props),
    );
  };
}

export const router = createBrowserRouter([
  {
    path: "/login",
    ErrorBoundary: RouteErrorBoundary,
    Component: lazyRoute(() => import("./pages/Login"), (m) => m.Login),
  },
  {
    path: "/",
    Component: AuthGuard,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      {
        Component: MainLayout,
        children: [
          { index: true, loader: () => redirect("/dashboard/executive-insights") },
          {
            path: "dashboard",
            Component: lazyRoute(() => import("./pages/DashboardRouter"), (m) => m.DashboardRouter),
          },
          {
            path: "dashboard/platform-owner",
            Component: lazyRoute(() => import("./pages/Dashboard"), (m) => m.Dashboard),
          },
          {
            path: "dashboard/security-posture-dashboard",
            Component: lazyRoute(() => import("./pages/CISODashboard"), (m) => m.CISODashboard),
          },
          {
            path: "dashboard/grc-manager",
            Component: lazyRoute(() => import("./pages/GRCManagerDashboard/GRCManagerDashboard"), (m) => m.GRCManagerDashboard,),
          },
          {
            path: "dashboard/group-auditor",
            Component: lazyRoute(() => import("./pages/GroupAuditorDashboard/GroupAuditorDashboard"), (m) => m.GroupAuditorDashboard,),
          },
          {
            path: "dashboard/system-owner",
            Component: lazyRoute(() => import("./pages/SystemOwnerDashboard/SystemOwnerDashboard"), (m) => m.SystemOwnerDashboard,),
          },
          {
            path: "dashboard/quadrant",
            Component: lazyRoute(() => import("./pages/QuadrantDashboard"), (m) => m.QuadrantDashboard),
          },
          {
            path: "dashboard/executive-insights",
            Component: lazyRoute(() => import("./pages/ExecutiveInsights"), (m) => m.ExecutiveInsights),
          },
          {
            path: "continuous-compliance/governance/catalog",
            Component: lazyRoute(() => import("./pages/Catalogs"), (m) => m.Catalogs),
          },
          {
            path: "continuous-compliance/governance/catalog/:id",
            Component: lazyRoute(() => import("./pages/Catalogs"), (m) => m.Catalogs),
          },
          {
            path: "continuous-compliance/governance/profile",
            Component: lazyRoute(() => import("./pages/Profiles"), (m) => m.Profiles),
          },
          {
            path: "continuous-compliance/governance/profile/:id",
            Component: lazyRoute(() => import("./pages/Profiles"), (m) => m.Profiles),
          },
          {
            path: "continuous-compliance/governance/control-mapping",
            Component: lazyRoute(() => import("./pages/Mappings"), (m) => m.Mappings),
          },
          {
            path: "continuous-compliance/governance/control-mapping/:id",
            Component: lazyRoute(() => import("./pages/Mappings"), (m) => m.Mappings),
          },
          {
            path: "continuous-compliance/system/component-definition",
            Component: lazyRoute(() => import("./pages/ComponentLibrary"), (m) => m.ComponentLibrary),
          },
          {
            path: "continuous-compliance/system/component-definition/:id",
            Component: lazyRoute(() => import("./pages/ComponentLibrary"), (m) => m.ComponentLibrary),
          },
          {
            path: "continuous-compliance/system/system-security-plan",
            Component: lazyRoute(() => import("./pages/SSPEditor"), (m) => m.SSPEditor),
          },
          {
            path: "continuous-compliance/system/system-security-plan/:id",
            Component: lazyRoute(() => import("./pages/SSPEditor"), (m) => m.SSPEditor),
          },
          {
            path: "continuous-compliance/assessment/assessment-plan",
            Component: lazyRoute(() => import("./pages/AssessmentPlans"), (m) => m.AssessmentPlans),
          },
          {
            path: "continuous-compliance/assessment/assessment-plan/wizard",
            Component: lazyRoute(() => import("./pages/AssessmentPlanWizard"), (m) => m.AssessmentPlanWizard),
          },
          {
            path: "continuous-compliance/assessment/assessment-plan/:id",
            Component: lazyRoute(() => import("./pages/AssessmentPlanDetail"), (m) => m.AssessmentPlanDetail),
          },
          {
            path: "continuous-compliance/assessment/assessment-execution",
            Component: lazyRoute(() => import("./pages/AssessmentExecutionList"), (m) => m.AssessmentExecutionList),
          },
          {
            path: "continuous-compliance/assessment/assessment-execution/:id",
            Component: lazyRoute(() => import("./pages/AssessmentExecution"), (m) => m.AssessmentExecution),
          },
          {
            path: "continuous-compliance/assessment/assessment-review",
            Component: lazyRoute(() => import("./pages/AssessmentReviewList"), (m) => m.AssessmentReviewList),
          },
          {
            path: "continuous-compliance/assessment/assessment-review/:id",
            Component: lazyRoute(() => import("./pages/AssessmentReview"), (m) => m.AssessmentReview),
          },
          {
            path: "continuous-compliance/assessment/assessment-result",
            Component: lazyRoute(() => import("./pages/AssessmentResults"), (m) => m.AssessmentResults),
          },
          {
            path: "continuous-compliance/assessment/assessment-result/:id",
            Component: lazyRoute(() => import("./pages/AssessmentResultDetail"), (m) => m.AssessmentResultDetail),
          },
          {
            path: "continuous-compliance/assessment/drift-management",
            Component: lazyRoute(() => import("./pages/grc/DriftAnalysisWorkspace"), (m) => m.DriftAnalysisWorkspace),
          },
          {
            path: "continuous-compliance/risk/poam",
            Component: lazyRoute(() => import("./pages/POAM/POAM"), (m) => m.POAM),
          },
          {
            path: "continuous-compliance/risk/poam/:id",
            Component: lazyRoute(() => import("./pages/POAMDetail"), (m) => m.POAMDetail),
          },
          {
            path: "continuous-compliance/risk/poam/:id/item/:itemId",
            Component: lazyRoute(() => import("./pages/POAMItemDetail"), (m) => m.POAMItemDetail),
          },
          {
            path: "continuous-compliance/scheduler",
            Component: lazyRoute(() => import("./pages/common-scheduler/SchedulerPage"), (m) => m.SchedulerPage),
          },
          {
            path: "privilege-governance-assurance",
            Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/PrivilegeAccessGovernancePage"), (m) => m.PrivilegeAccessGovernancePage,),
            children: [
              {
                index: true,
                loader: () => redirect("/privilege-governance-assurance/privilege-access-insights"),
              },
              {
                path: "privilege-access-insights",
                Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/UserAccessGovernance"), (m) => m.UserAccessGovernance,),
              },
              {
                path: "privilege-governance-assurance",
                Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/UserAccessGovernance"), (m) => m.UserAccessGovernance,),
              },
              {
                path: "user-access-review",
                Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/UserAccessGovernance"), (m) => m.UserAccessGovernance,),
              },
              {
                path: "privilege-insights",
                Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/PrivilegeAccessInsights"), (m) => m.PrivilegeAccessInsights,),
              },
              {
                path: "access-review",
                Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/AccessReview"), (m) => m.AccessReview,),
              },
              {
                path: "access-governance",
                Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/AccessGovernance"), (m) => m.AccessGovernance,),
              },
              {
                path: "risk-configuration",
                loader: () => redirect("/privilege-governance-assurance/account-configuration"),
              },
              {
                path: "account-configuration",
                Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/PrivilegeAccountConfiguration"), (m) => m.PrivilegeAccountConfiguration,),
              },
              {
                path: "pga-configuration",
                Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/PrivilegeAccountConfiguration"), (m) => m.PrivilegeAccountConfiguration,),
              },
              {
                path: "schedulers",
                Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/PgaScheduler"), (m) => m.PgaScheduler,),
              },
              {
                path: "iam-policy-categorization",
                loader: () =>
                  redirect("/privilege-governance-assurance/account-configuration?tab=iam-policy-categorization"),
              },
            ],
          },
          {
            path: "privilege-access-governance/*",
            loader: ({ request }) => {
              const url = new URL(request.url);
              const rest = url.pathname.replace(/^\/privilege-access-governance/, "");
              return redirect(`/privilege-governance-assurance${rest || "/privilege-access-insights"}`);
            },
          },
          {
            path: "report",
            Component: lazyRoute(() => import("./pages/ReportsList"), (m) => m.ReportsList),
          },
          {
            path: "report/:sspId",
            Component: lazyRoute(() => import("./pages/ReportDetails"), (m) => m.ReportDetails),
          },
          {
            path: "audit-report",
            Component: lazyRoute(() => import("./pages/AuditReport/AuditReport"), (m) => m.AuditReport),
          },
          {
            path: "audit-report/:sspId",
            Component: lazyRoute(() => import("./pages/AuditReport/AuditReportDetail"), (m) => m.AuditReportDetail),
          },
          // One assessment plan's report — same page, assessment-scoped stream.
          {
            path: "audit-report/:sspId/assessment/:assessmentId",
            Component: lazyRoute(() => import("./pages/AuditReport/AuditReportDetail"), (m) => m.AuditReportDetail),
          },
          {
            path: "system-owner-report",
            Component: lazyRoute(() => import("./pages/SystemOwnerReport/SystemOwnerReport"), (m) => m.SystemOwnerReport,),
          },
          {
            path: "system-owner-report/:sspId",
            Component: lazyRoute(() => import("./pages/SystemOwnerReport/SystemOwnerReportDetail"), (m) => m.SystemOwnerReportDetail,),
          },
          {
            path: "telemetry",
            Component: lazyRoute(() => import("./pages/Telemetry"), (m) => m.Telemetry),
          },
          {
            path: "continuous-compliance/governance/content-registry",
            Component: lazyRoute(() => import("./pages/ContentRegistry"), (m) => m.ContentRegistry),
          },
          {
            path: "continuous-compliance/governance/content-registry/console",
            Component: lazyRoute(() => import("./pages/ContentRegistry"), (m) => m.ProcessingConsolePage),
          },
          {
            path: "continuous-compliance/governance/content-registry/console-3d",
            Component: lazyRoute(() => import("./pages/ContentRegistry"), (m) => m.ProcessingConsole3DPage),
          },
          {
            path: "continuous-compliance/governance/content-registry/:id",
            Component: lazyRoute(() => import("./pages/ContentRegistry"), (m) => m.ContentRegistry),
          },
          {
            path: "governance",
            Component: lazyRoute(() => import("./pages/Governance"), (m) => m.Governance),
          },
          {
            path: "configuration/audit-log",
            Component: lazyRoute(() => import("./pages/AuditLogs"), (m) => m.AuditLogs),
          },
          {
            path: "configuration/administration/schedulers/:id/configuration",
            Component: lazyRoute(() => import("./pages/SchedulerConfiguration"), (m) => m.SchedulerConfiguration),
          },
          {
            path: "user-management",
            Component: lazyRoute(() => import("./pages/Users"), (m) => m.Users),
          },
          {
            path: "setting",
            Component: lazyRoute(() => import("./pages/Settings"), (m) => m.Settings),
          },
          {
            path: "configuration/administration/license-management",
            Component: lazyRoute(() => import("./pages/LicenseManagement"), (m) => m.LicenseManagement),
          },
          {
            path: "component-definition-visualizer",
            Component: lazyRoute(() => import("./pages/visualizer/ComponentDefinitionVisualizer"), (m) => m.ComponentDefinitionVisualizer,),
          },
          {
            path: "configuration/cmdb/applications",
            Component: lazyRoute(() => import("./pages/CMDBApplications"), (m) => m.CMDBApplications),
          },
          {
            path: "configuration/cmdb/applications/:id",
            Component: lazyRoute(() => import("./pages/CMDBApplicationDetail"), (m) => m.CMDBApplicationDetail),
          },
          {
            path: "configuration/cmdb/applications/:applicationId/assets/:assetId",
            Component: lazyRoute(() => import("./pages/CMDBApplicationAssetDetail"), (m) => m.CMDBApplicationAssetDetail,),
          },
          {
            path: "configuration/cmdb/assets",
            Component: lazyRoute(() => import("./pages/CMDBAssets"), (m) => m.CMDBAssets),
          },
          {
            path: "configuration/cmdb/devices",
            Component: lazyRoute(() => import("./pages/CMDBConfigItems"), (m) => m.CMDBConfigItems),
          },
          {
            path: "configuration/cmdb/devices/:id",
            Component: lazyRoute(() => import("./pages/CMDBDeviceDetail"), (m) => m.CMDBDeviceDetail),
          },
          {
            path: "configuration/cmdb/services",
            Component: lazyRoute(() => import("./pages/CMDBServices"), (m) => m.CMDBServices),
          },
          {
            path: "configuration/cmdb/services/:id",
            Component: lazyRoute(() => import("./pages/CMDBServiceDetail"), (m) => m.CMDBServiceDetail),
          },
          {
            path: "configuration/cmdb/cloud-accounts",
            Component: lazyRoute(() => import("./pages/CMDBCloudAccounts"), (m) => m.CMDBCloudAccounts),
          },
          {
            path: "configuration/cmdb/cloud-accounts/:id",
            Component: lazyRoute(() => import("./pages/CMDBCloudAccountDetail"), (m) => m.CMDBCloudAccountDetail),
          },
          {
            path: "configuration/settings/directory-configuration",
            Component: lazyRoute(() => import("./pages/DirectoryConfiguration"), (m) => m.DirectoryConfiguration),
          },
          {
            path: "configuration/administration/organization",
            Component: lazyRoute(() => import("./pages/OrganizationManagement"), (m) => m.OrganizationManagement),
          },
          {
            path: "configuration/administration/roles",
            Component: lazyRoute(() => import("./pages/RoleManagement"), (m) => m.RoleManagement),
          },
          {
            path: "configuration/administration/users",
            Component: lazyRoute(() => import("./pages/UserManagement"), (m) => m.UserManagement),
          },
          {
            path: "configuration/administration/llm-configuration",
            Component: lazyRoute(() => import("./pages/LlmConfigurations"), (m) => m.LlmConfigurations),
          },
          {
            path: "configuration/administration/ai-module-configuration",
            Component: lazyRoute(() => import("./pages/AiInteractionController"), (m) => m.AiInteractionController),
          },
          {
            path: "configuration/administration/schedulers",
            Component: lazyRoute(() => import("./pages/schedulers/Schedulers"), (m) => m.Schedulers),
          },
          {
            path: "configuration/administration/tools",
            Component: lazyRoute(() => import("./pages/tools/Tools"), (m) => m.Tools),
          },
          {
            path: "configuration/administration/tools/instance/:toolName?",
            Component: lazyRoute(() => import("./pages/tools/ToolsVendorTable"), (m) => m.ToolsVendorTable),
          },
          {
            path: "configuration/administration/tools/config/:toolConfigId",
            Component: lazyRoute(() => import("./pages/tools/toolConfig/ToolConfig"), (m) => m.ToolConfig),
          },
          {
            path: "configuration/administration/severity-colors",
            Component: lazyRoute(() => import("./pages/severityColors/SeverityColors"), (m) => m.SeverityColors),
          },
          {
            path: "configuration/administration/validation-rules",
            Component: lazyRoute(() => import("./pages/ValidationRules"), (m) => m.ValidationRules),
          },
          {
            path: "configuration/administration/alert-calendar",
            Component: lazyRoute(() => import("./pages/AlertCalendar/AlertCalendar"), (m) => m.AlertCalendar),
          },
          {
            path: "configuration/administration/iam-policy-categorization",
            Component: lazyRoute(() => import("./pages/PrivilegeAccessGovernance/IamPolicyCategorization"), (m) => m.IamPolicyCategorization,),
          },
          {
            // Moved to Change Governance Assurance Validation; old links keep working.
            path: "configuration/administration/ai-analysis/*",
            loader: ({ params }) =>
              redirect(
                `/change-governance-assurance-validation/change-request-assessment/${params["*"] ?? ""}`,
              ),
          },
          {
            path: "configuration/administration/insights-configuration",
            Component: lazyRoute(() => import("./pages/DashboardConfig/DashboardConfig"), (m) => m.DashboardConfig),
          },
          {
            path: "architecture-compliance",
            Component: lazyRoute(() => import("./pages/ArchitectureCompliance"), (m) => m.ArchitectureCompliance),
          },

          {
            path: "vulnerability-management/overview",
            Component: lazyRoute(() => import("./pages/ComplianceDashboard"), (m) => m.VmOverview),
          },
          {
            path: "vulnerability-management/dashboard",
            Component: lazyRoute(() => import("./pages/ComplianceDashboard"), (m) => m.VmDashboard),
          },
          {
            path: "vulnerability-management/configuration",
            Component: lazyRoute(() => import("./pages/ComplianceDashboard"), (m) => m.VmToolsConfiguration),
          },
          {
            path: "vulnerability-management/remediation-policy",
            loader: () => redirect("/vulnerability-management/configuration"),
          },
          {
            path: "vulnerability-management/schedulers",
            Component: lazyRoute(() => import("./pages/ComplianceDashboard"), (m) => m.VmSchedulers),
          },
          {
            path: "configuration-management/overview",
            Component: lazyRoute(() => import("./pages/ComplianceDashboard"), (m) => m.CmOverview),
          },
          {
            path: "configuration-management/dashboard",
            Component: lazyRoute(() => import("./pages/ComplianceDashboard"), (m) => m.CmDashboard),
          },
          {
            path: "configuration-management/configuration",
            Component: lazyRoute(() => import("./pages/ComplianceDashboard"), (m) => m.CmToolsConfiguration),
          },
          {
            path: "configuration-management/remediation-policy",
            loader: () => redirect("/configuration-management/configuration"),
          },
          {
            path: "configuration-management/schedulers",
            Component: lazyRoute(() => import("./pages/ComplianceDashboard"), (m) => m.CmSchedulers),
          },
          { path: "vulnerability-management", loader: () => redirect("/vulnerability-management/overview") },
          { path: "configuration-management", loader: () => redirect("/configuration-management/overview") },
          {
            path: "it-compliance",
            loader: () => redirect("/vulnerability-management/overview"),
          },
          {
            path: "ciso-insights",
            Component: lazyRoute(() => import("./pages/CisoInsights"), (m) => m.CisoInsights),
          },

          // ── Change Governance Assurance Validation ──────────────────────────────
          // TODO(rbac): gate these three routes once Log Review modules land in RBAC_MATRIX.
          {
            path: "change-governance-assurance-validation",
            Component: lazyRoute(() => import("./pages/LogReview/LogReviewPage"), (m) => m.LogReviewPage),
          },
          {
            path: "change-governance-assurance-validation-insights",
            Component: lazyRoute(() => import("./pages/LogReviewerInsights/LogReviewerInsightsPage"), (m) => m.LogReviewerInsightsPage,),
          },
          {
            path: "change-governance-assurance-validation-ciso",
            Component: lazyRoute(() => import("./pages/CisoLogReview/CisoLogReviewPage"), (m) => m.CisoLogReviewPage),
          },

          // ── Security Tools Operations Center ──────────────────────────
          {
            path: "security-tools/agent-health",
            Component: lazyRoute(() => import("./pages/AgentHealth"), (m) => m.AgentHealth),
          },
          {
            path: "security-tools/asset-coverage-onboarding",
            Component: lazyRoute(() => import("./pages/AssetCoverageOnboarding"), (m) => m.AssetCoverageOnboarding),
          },
          {
            path: "security-tools/scan-coverage",
            Component: lazyRoute(() => import("./pages/ScanCoverage"), (m) => m.ScanCoverage),
          },
          {
            path: "security-tools/configuration",
            Component: lazyRoute(() => import("./pages/SecurityToolsConfiguration"), (m) => m.SecurityToolsConfiguration,),
          },
          // ── Change Assurance Governance ─────────────────────────────────
          // One destination with three routed tabs behind it. Nested rather
          // than three sibling paths: `/change-governance-assurance-validation-ciso`
          // shares a prefix with the base, which is what made every sibling
          // light up together in the sidebar.
          //
          // TODO(rbac): gate this subtree on change-governance-assurance-validation once
          // the Log Review modules land in RBAC_MATRIX.
          {
            path: "change-governance-assurance-validation",
            Component: lazyRoute(() => import("./pages/ChangeAssuranceGovernance/ChangeAssuranceGovernancePage"), (m) => m.ChangeAssuranceGovernancePage,),
            children: [
              {
                index: true,
                Component: lazyRoute(() => import("./pages/LogReview/LogReviewPage"), (m) => m.LogReviewPage),
              },
              {
                path: "reviewer-insights",
                Component: lazyRoute(() => import("./pages/LogReviewerInsights/LogReviewerInsightsPage"), (m) => m.LogReviewerInsightsPage,),
              },
              {
                path: "ciso-insights",
                Component: lazyRoute(() => import("./pages/CisoLogReview/CisoLogReviewPage"), (m) => m.CisoLogReviewPage,),
              },
            ],
          },
          {
            path: "change-governance-assurance-validation/change-request-assessment",
            Component: lazyRoute(() => import("./pages/ChangeGovernanceAssuranceReadiness/pages/CGARList"), (m) => m.default,),
          },
          {
            path: "change-governance-assurance-validation/change-request-assessment/:crId",
            Component: lazyRoute(() => import("./pages/ChangeGovernanceAssuranceReadiness/pages/CGARDetails"), (m) => m.CGARDetails || m.default,),
          },
          {
            path: "change-governance-assurance-validation/rules",
            Component: lazyRoute(() => import("./pages/ChangeGovernanceAssuranceReadiness/pages/CGARrulemanagement"), (m) => m.default,),
          },
          {
            path: "demo/assessment-plan-full-trigger",
            Component: lazyRoute(() => import("./pages/AssessmentPlanFullDemo"), (m) => m.AssessmentPlanFullDemo,),
          },
          {
            path: "cmdb/*",
            loader: ({ params, request }) =>
              redirect(
                `/configuration/cmdb/${params["*"] ?? ""}${new URL(request.url).search}`,
              ),
          },
          {
            path: "administration/*",
            loader: ({ params, request }) =>
              redirect(
                `/configuration/administration/${params["*"] ?? ""}${new URL(request.url).search}`,
              ),
          },
          {
            path: "administration/audit-log",
            loader: () => redirect("/configuration/audit-log"),
          },
          {
            path: "admin/validation-rules",
            loader: () => redirect("/configuration/administration/validation-rules"),
          },
        ],
      },
    ],
  },
  {
    // An unmatched URL must never silently bounce an authenticated user back to
    // the login form: that is what turned a typo'd redirect target into an
    // invisible "login does nothing" bug — no error, no toast, just the form
    // again. Only visitors without a session belong on /login. Authenticated
    // users go back to the last page that actually rendered (tracked in
    // MainLayout), falling back to the default dashboard if none is recorded.
    path: "*",
    loader: () => {
      if (!useAuthStore.getState().isAuthenticated) return redirect("/login");
      let lastVisited: string | null = null;
      try {
        if (typeof sessionStorage !== "undefined") {
          lastVisited = sessionStorage.getItem("lastVisitedPath");
        }
      } catch {
        lastVisited = null;
      }
      return redirect(lastVisited || "/dashboard/executive-insights");
    },
  },
]);
